import React, { useState, useRef } from 'react';

import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import i18n from 'i18n-js';
import {
    Button,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    FormHelperText,
    Box,
    Grid,
} from '@material-ui/core';

import { useStyles } from '../styles';
import Input from 'components/shared/components/Input';
import agent from 'api/agent';
import {
    RestaurantPaymentProvider,
    RestaurantPaymentProviderSettings,
    RestaurantPaymentSettings,
} from 'store/restaurantsStore/reducer';
import { useDispatch } from 'react-redux';
import globalStore from 'store/globalStore';
import restaurantsStore from 'store/restaurantsStore';
import { useHistory } from 'react-router-dom';
import { regexHelper } from 'helpers/regexHelper';
import { PopupConfirmation } from 'components/shared';

type FieldName = 'scope' | 'identity' | 'secret';

const CUSTOM_PROVIDER = 'CUSTOM';
const MAX_CUSTOM_PROVIDERS = 3;

const getRequiredErrorMessage = (name: string) => i18n.t('form.errors.required', { name });
const getCustomProviderNameLabel = () => i18n.t('form.customProviderName');
const getCustomProviderLabel = () => i18n.t('restaurant.customProvider');
const getCustomProviderNameRequiredError = () =>
    getRequiredErrorMessage(getCustomProviderNameLabel());
const getCustomProviderNameMaxLengthError = () =>
    i18n.t('form.errors.maxLength', {
        name: getCustomProviderNameLabel(),
        length: 50,
    });
const getLettersAndNumbersOnlyError = () => i18n.t('form.errors.lettersAndNumbersOnly');
const getAlreadyTakenError = () => i18n.t('form.errors.alreadyTaken');
const getMaxCustomProvidersError = () =>
    i18n.t('form.errors.maxCustomProviders', { count: MAX_CUSTOM_PROVIDERS });
const getCustomProviderDeleteWarning = () => i18n.t('warnings.removeCustomProviderWarning');

const providerFieldMap: Record<RestaurantPaymentProvider, Record<FieldName, string>> = {
    [RestaurantPaymentProvider.Wallee]: {
        scope: 'Space Id',
        identity: 'Application User Id',
        secret: 'Authentication Key',
    },
    [RestaurantPaymentProvider.Datatrans]: {
        scope: 'Scope',
        identity: 'Username',
        secret: 'Password',
    },
    [RestaurantPaymentProvider.PayTec]: {
        scope: 'Scope',
        identity: 'Identity',
        secret: 'Secret',
    },
    [RestaurantPaymentProvider.Softpay]: {
        scope: 'Scope',
        identity: 'Identity',
        secret: 'Secret',
    },
};

interface CustomProviderEntry {
    providerName: string;
}

export function getProviderNameForForm(
    provider?: RestaurantPaymentProviderSettings,
    defaultValue: string = RestaurantPaymentProvider.Wallee
): string {
    if (provider?.isCustomProvider || provider?.customProviders?.length) {
        return CUSTOM_PROVIDER;
    }

    if (
        provider?.providerName &&
        Object.values(RestaurantPaymentProvider).includes(
            provider.providerName as RestaurantPaymentProvider
        )
    ) {
        return provider.providerName as RestaurantPaymentProvider;
    }

    return defaultValue;
}

export function getInitialCustomProviders(
    provider?: RestaurantPaymentProviderSettings
): CustomProviderEntry[] {
    const customProviders = (provider?.customProviders || []).filter((entry) =>
        Boolean(entry?.providerName?.trim())
    );

    if (provider?.isCustomProvider) {
        const primaryProviderName = provider.providerName?.trim() || '';
        if (primaryProviderName) {
            return [
                { providerName: primaryProviderName },
                ...customProviders.map((entry) => ({ providerName: entry.providerName.trim() })),
            ];
        }

        if (customProviders.length > 0) {
            return customProviders.map((entry) => ({ providerName: entry.providerName.trim() }));
        }

        return [{ providerName: '' }];
    }

    return [];
}

interface ProviderFields {
    providerName: string;
    customProviders: CustomProviderEntry[];
    scope?: string;
    identity: string;
    secret: string;
}

interface FormValues {
    posProvider: ProviderFields;
    webProvider: ProviderFields;
}

interface DeleteProviderTarget {
    section: 'posProvider' | 'webProvider';
    index: number;
}

const customProviderEntryValidation = Yup.object().shape({
    providerName: Yup.string()
        .trim()
        .required(() => getCustomProviderNameRequiredError())
        .min(1, () => getCustomProviderNameRequiredError())
        .max(50, () => getCustomProviderNameMaxLengthError())
        .matches(regexHelper.numbersAndAllLetters, () => getLettersAndNumbersOnlyError()),
});

const providerValidation = Yup.object().shape({
    providerName: Yup.string().required(() => getRequiredErrorMessage(i18n.t('form.provider'))),
    customProviders: Yup.array().when('providerName', {
        is: CUSTOM_PROVIDER,
        then: (schema: Yup.ArraySchema<any>) =>
            schema
                .of(customProviderEntryValidation)
                .min(1, () => getCustomProviderNameRequiredError())
                .test(
                    'custom-providers-unique',
                    () => getAlreadyTakenError(),
                    (value: any) => {
                        const entries = (
                            Array.isArray(value) ? value : []
                        ) as CustomProviderEntry[];
                        const names = entries.map((item) =>
                            (item?.providerName || '').trim().toLowerCase()
                        );
                        return names.length === new Set(names).size;
                    }
                )
                .test(
                    'custom-providers-not-empty',
                    () => getCustomProviderNameRequiredError(),
                    (value: any) => {
                        const entries = (
                            Array.isArray(value) ? value : []
                        ) as CustomProviderEntry[];
                        return entries.some((item) => (item?.providerName || '').trim().length > 0);
                    }
                )
                .test(
                    'custom-providers-max-count',
                    () => getMaxCustomProvidersError(),
                    (value: any) => {
                        const entries = (
                            Array.isArray(value) ? value : []
                        ) as CustomProviderEntry[];
                        return entries.length <= MAX_CUSTOM_PROVIDERS;
                    }
                ),
        otherwise: (schema: Yup.ArraySchema<any>) => schema.notRequired(),
    }),
    scope: Yup.string().when(
        'providerName',
        (providerName: RestaurantPaymentProvider, schema: any) => {
            if (providerName === RestaurantPaymentProvider.Wallee) {
                return schema
                    .required(() => getRequiredErrorMessage(providerFieldMap[providerName].scope))
                    .trim()
                    .min(1, () => getRequiredErrorMessage(providerFieldMap[providerName].scope));
            }
            return schema.notRequired();
        }
    ),
    identity: Yup.string().when(
        'providerName',
        (providerName: RestaurantPaymentProvider, schema: any) => {
            if (
                providerName === RestaurantPaymentProvider.Datatrans ||
                providerName === RestaurantPaymentProvider.Wallee
            ) {
                return schema
                    .required(() =>
                        getRequiredErrorMessage(providerFieldMap[providerName].identity)
                    )
                    .trim()
                    .min(1, () => getRequiredErrorMessage(providerFieldMap[providerName].identity));
            }
            return schema.notRequired();
        }
    ),
    secret: Yup.string().when(
        'providerName',
        (providerName: RestaurantPaymentProvider, schema: any) => {
            if (
                providerName === RestaurantPaymentProvider.Datatrans ||
                providerName === RestaurantPaymentProvider.Wallee
            ) {
                return schema
                    .required(() => getRequiredErrorMessage(providerFieldMap[providerName].secret))
                    .trim()
                    .min(1, () => getRequiredErrorMessage(providerFieldMap[providerName].secret));
            }
            return schema.notRequired();
        }
    ),
});

const webProviderValidation = providerValidation.clone().shape({
    providerName: Yup.string().notRequired(),
});

const validationSchema = Yup.object().shape({
    posProvider: providerValidation,
    webProvider: webProviderValidation,
});

interface PaymentProvidersProps {
    restaurantId: string;
    paymentProviders?: RestaurantPaymentSettings;
    restaurantName: string;
}

export default function PaymentProviders(props: PaymentProvidersProps) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const history = useHistory();
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);
    const [customProviderToDelete, setCustomProviderToDelete] =
        useState<DeleteProviderTarget | null>(null);
    const formikRef = useRef<any>(null);

    function getFieldLabel(provider: RestaurantPaymentProvider, field: FieldName): string {
        return providerFieldMap[provider][field];
    }

    const initialValues: FormValues = {
        posProvider: {
            providerName: getProviderNameForForm(props.paymentProviders?.posPaymentProvider),
            customProviders: getInitialCustomProviders(props.paymentProviders?.posPaymentProvider),
            scope: props.paymentProviders?.posPaymentProvider?.credentials?.scope || '',
            identity: props.paymentProviders?.posPaymentProvider?.credentials?.identity || '',
            secret: props.paymentProviders?.posPaymentProvider?.credentials?.secret || '',
        },
        webProvider: {
            providerName: getProviderNameForForm(
                props.paymentProviders?.webPaymentProvider,
                props.paymentProviders?.webPaymentProvider?.providerName || ''
            ),
            customProviders: [],
            scope: props.paymentProviders?.webPaymentProvider?.credentials?.scope || '',
            identity: props.paymentProviders?.webPaymentProvider?.credentials?.identity || '',
            secret: props.paymentProviders?.webPaymentProvider?.credentials?.secret || '',
        },
    };

    const handleFormSubmit = (values: FormValues, { setSubmitting }: any) => {
        const posCustomProviderEntries = (values.posProvider.customProviders || [])
            .filter((entry) => entry.providerName.trim().length > 0)
            .map((entry) => ({ providerName: entry.providerName.trim() }));
        const primaryPosCustomProviderName = posCustomProviderEntries[0]?.providerName || '';
        const additionalPosCustomProviders = posCustomProviderEntries.slice(1);

        const model: RestaurantPaymentSettings = {
            posPaymentProvider: {
                providerName:
                    values.posProvider.providerName === CUSTOM_PROVIDER
                        ? primaryPosCustomProviderName
                        : values.posProvider.providerName || '',
                isCustomProvider: values.posProvider.providerName === CUSTOM_PROVIDER,
                customProviders:
                    values.posProvider.providerName === CUSTOM_PROVIDER
                        ? additionalPosCustomProviders
                        : [],
                credentials: {
                    scope: values.posProvider.scope || '',
                    identity: values.posProvider.identity,
                    secret: values.posProvider.secret,
                },
            },
            webPaymentProvider: {
                providerName:
                    values.webProvider.providerName === CUSTOM_PROVIDER
                        ? values.webProvider.customProviders[0]?.providerName || ''
                        : values.webProvider.providerName || '',
                isCustomProvider: values.webProvider.providerName === CUSTOM_PROVIDER,
                customProviders: [],
                credentials: {
                    scope: values.webProvider.scope || '',
                    identity: values.webProvider.identity,
                    secret: values.webProvider.secret,
                },
            },
        };

        dispatch(globalStore.actionCreators.showSpiner());
        agent.Restaurants.SetPaymentSettings(props.restaurantId, model)
            .then((res) => {
                dispatch(
                    restaurantsStore.actionCreators.setPaymentSettings(props.restaurantId, res)
                );
                dispatch(
                    globalStore.actionCreators.showToaster(
                        'success',
                        i18n.t('messages.updatedSuccesfully')
                    )
                );
                history.push(`/restaurants`);
            })
            .catch((e) => {
                dispatch(globalStore.actionCreators.showToaster('error', e));
            })
            .finally(() => {
                dispatch(globalStore.actionCreators.hideSpiner());
                setSubmitting(false);
            });
    };

    const removeCustomProvider = () => {
        const formikValues = formikRef.current?.values as FormValues | undefined;
        if (!customProviderToDelete || !formikValues) {
            return;
        }

        const nextProviders = [
            ...(formikValues[customProviderToDelete.section].customProviders || []),
        ];
        nextProviders.splice(customProviderToDelete.index, 1);
        formikRef.current?.setFieldValue(
            `${customProviderToDelete.section}.customProviders`,
            nextProviders
        );
        setShowDeleteConfirmationModal(false);
        setCustomProviderToDelete(null);
        setTimeout(() => {
            formikRef.current?.validateForm();
        }, 0);
    };

    return (
        <>
            <Formik
                innerRef={formikRef}
                initialValues={initialValues}
                validationSchema={validationSchema}
                enableReinitialize
                onSubmit={handleFormSubmit}
            >
                {({
                    values,
                    handleChange,
                    handleBlur,
                    touched,
                    errors,
                    isValid,
                    isSubmitting,
                    dirty,
                    setFieldValue,
                    setFieldTouched,
                    validateForm,
                }) => {
                    const getCustomProviderFieldError = (
                        section: 'posProvider' | 'webProvider',
                        index: number
                    ) => {
                        const sectionErrors = errors[section]?.customProviders as
                            | Array<{ providerName?: string }>
                            | undefined;
                        if (Array.isArray(sectionErrors)) {
                            return sectionErrors[index]?.providerName;
                        }
                        return undefined;
                    };

                    const getCustomProviderFieldTouched = (
                        section: 'posProvider' | 'webProvider',
                        index: number
                    ) => {
                        const sectionTouched = touched[section]?.customProviders;
                        if (Array.isArray(sectionTouched)) {
                            return Boolean(sectionTouched[index]?.providerName);
                        }
                        return false;
                    };

                    const handleProviderChange =
                        (section: 'posProvider' | 'webProvider') =>
                        async (event: React.ChangeEvent<{ value: unknown }>) => {
                            const providerName = event.target.value as string;
                            setFieldValue(`${section}.providerName`, providerName);
                            setFieldValue(
                                `${section}.customProviders`,
                                providerName === CUSTOM_PROVIDER && section === 'posProvider'
                                    ? [{ providerName: '' }]
                                    : []
                            );
                            setFieldTouched(`${section}.providerName`, true);
                            setFieldTouched(`${section}.scope`, true);
                            setFieldTouched(`${section}.identity`, true);
                            setFieldTouched(`${section}.secret`, true);

                            setFieldValue(`${section}.scope`, '');
                            setFieldValue(`${section}.identity`, '');
                            setFieldValue(`${section}.secret`, '');

                            setTimeout(() => {
                                validateForm();
                            }, 0);
                        };

                    const addCustomProvider = (section: 'posProvider' | 'webProvider') => {
                        const nextProviders = [
                            ...(values[section].customProviders || []),
                            { providerName: '' },
                        ];
                        setFieldValue(`${section}.customProviders`, nextProviders);
                        const nextIndex = nextProviders.length - 1;
                        setFieldTouched(
                            `${section}.customProviders[${nextIndex}].providerName`,
                            true
                        );
                        setTimeout(() => {
                            validateForm();
                        }, 0);
                    };

                    const requestRemoval = (
                        section: 'posProvider' | 'webProvider',
                        index: number
                    ) => {
                        setCustomProviderToDelete({ section, index });
                        setShowDeleteConfirmationModal(true);
                    };

                    return (
                        <Form className={classes.formInnerContainer} style={{ width: 700 }}>
                            <Grid container spacing={4}>
                                <Grid item xs={12} md={6}>
                                    <h6 className={classes.sectionTitle}>
                                        {i18n.t('restaurant.posProvider').toUpperCase()}
                                    </h6>
                                    <Box mb={2} />
                                    <FormControl
                                        className={classes.inputContainer}
                                        error={Boolean(
                                            touched.posProvider?.providerName &&
                                                errors.posProvider?.providerName
                                        )}
                                        fullWidth
                                    >
                                        <InputLabel
                                            id="pos-provider-label"
                                            style={{ marginLeft: 12, marginTop: 5 }}
                                        >
                                            {i18n.t('form.provider')}
                                        </InputLabel>
                                        <Select
                                            variant="filled"
                                            labelId="pos-provider-label"
                                            id="posProvider.providerName"
                                            name="posProvider.providerName"
                                            value={values.posProvider.providerName}
                                            onChange={handleProviderChange('posProvider')}
                                            onBlur={handleBlur}
                                            autoWidth
                                        >
                                            <MenuItem value={RestaurantPaymentProvider.Wallee}>
                                                {RestaurantPaymentProvider.Wallee}
                                            </MenuItem>
                                            <MenuItem value={RestaurantPaymentProvider.Datatrans}>
                                                {RestaurantPaymentProvider.Datatrans}
                                            </MenuItem>
                                            <MenuItem value={RestaurantPaymentProvider.PayTec}>
                                                {RestaurantPaymentProvider.PayTec}
                                            </MenuItem>
                                            <MenuItem value={RestaurantPaymentProvider.Softpay}>
                                                {RestaurantPaymentProvider.Softpay}
                                            </MenuItem>
                                            <MenuItem value={CUSTOM_PROVIDER}>
                                                {getCustomProviderLabel()}
                                            </MenuItem>
                                        </Select>
                                        <FormHelperText>
                                            {errors.posProvider?.providerName}
                                        </FormHelperText>
                                    </FormControl>

                                    {values.posProvider.providerName === CUSTOM_PROVIDER && (
                                        <>
                                            {values.posProvider.customProviders.map(
                                                (provider, index) => (
                                                    <Box key={`pos-${index}`} mb={2}>
                                                        <Input
                                                            variant="filled"
                                                            name={`posProvider.customProviders[${index}].providerName`}
                                                            label={
                                                                <span>
                                                                    {i18n.t(
                                                                        'form.customProviderName'
                                                                    )}
                                                                </span>
                                                            }
                                                            type="text"
                                                            value={provider.providerName}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            error={
                                                                getCustomProviderFieldTouched(
                                                                    'posProvider',
                                                                    index
                                                                )
                                                                    ? getCustomProviderFieldError(
                                                                          'posProvider',
                                                                          index
                                                                      )
                                                                    : undefined
                                                            }
                                                            helperText={
                                                                getCustomProviderFieldTouched(
                                                                    'posProvider',
                                                                    index
                                                                )
                                                                    ? getCustomProviderFieldError(
                                                                          'posProvider',
                                                                          index
                                                                      )
                                                                    : undefined
                                                            }
                                                            className={classes.inputContainer}
                                                        />
                                                        <Button
                                                            type="button"
                                                            color="secondary"
                                                            onClick={() =>
                                                                requestRemoval('posProvider', index)
                                                            }
                                                        >
                                                            {i18n.t('common.remove')}
                                                        </Button>
                                                    </Box>
                                                )
                                            )}
                                            {typeof errors.posProvider?.customProviders ===
                                                'string' && (
                                                <FormHelperText error>
                                                    {errors.posProvider.customProviders}
                                                </FormHelperText>
                                            )}
                                            {values.posProvider.customProviders.length <
                                                MAX_CUSTOM_PROVIDERS && (
                                                <Button
                                                    type="button"
                                                    variant="outlined"
                                                    color="primary"
                                                    onClick={() => addCustomProvider('posProvider')}
                                                >
                                                    {i18n.t('restaurant.addCustomProvider')}
                                                </Button>
                                            )}
                                        </>
                                    )}

                                    {values.posProvider.providerName ===
                                        RestaurantPaymentProvider.Wallee && (
                                        <>
                                            <Input
                                                name="posProvider.identity"
                                                label={
                                                    <span>
                                                        {getFieldLabel(
                                                            values.posProvider.providerName,
                                                            'identity'
                                                        )}
                                                    </span>
                                                }
                                                type="text"
                                                variant="filled"
                                                error={
                                                    touched.posProvider?.identity
                                                        ? errors.posProvider?.identity
                                                        : undefined
                                                }
                                                helperText={
                                                    touched.posProvider?.identity
                                                        ? errors.posProvider?.identity
                                                        : undefined
                                                }
                                                className={classes.inputContainer}
                                            />
                                            <Input
                                                name="posProvider.secret"
                                                label={
                                                    <span>
                                                        {getFieldLabel(
                                                            values.posProvider.providerName,
                                                            'secret'
                                                        )}
                                                    </span>
                                                }
                                                type="password"
                                                variant="filled"
                                                error={
                                                    touched.posProvider?.secret
                                                        ? errors.posProvider?.secret
                                                        : undefined
                                                }
                                                helperText={
                                                    touched.posProvider?.secret
                                                        ? errors.posProvider?.secret
                                                        : undefined
                                                }
                                                className={classes.inputContainer}
                                            />
                                            <Input
                                                name="posProvider.scope"
                                                label={
                                                    <span>
                                                        {getFieldLabel(
                                                            values.posProvider.providerName,
                                                            'scope'
                                                        )}
                                                    </span>
                                                }
                                                variant="filled"
                                                error={
                                                    touched.posProvider?.scope
                                                        ? errors.posProvider?.scope
                                                        : undefined
                                                }
                                                helperText={
                                                    touched.posProvider?.scope
                                                        ? errors.posProvider?.scope
                                                        : undefined
                                                }
                                                className={classes.inputContainer}
                                            />
                                        </>
                                    )}

                                    {values.posProvider.providerName ===
                                        RestaurantPaymentProvider.Datatrans && (
                                        <>
                                            <Input
                                                name="posProvider.identity"
                                                label={
                                                    <span>
                                                        {getFieldLabel(
                                                            values.posProvider.providerName,
                                                            'identity'
                                                        )}
                                                    </span>
                                                }
                                                variant="filled"
                                                error={
                                                    touched.posProvider?.identity
                                                        ? errors.posProvider?.identity
                                                        : undefined
                                                }
                                                helperText={
                                                    touched.posProvider?.identity
                                                        ? errors.posProvider?.identity
                                                        : undefined
                                                }
                                                className={classes.inputContainer}
                                            />
                                            <Input
                                                name="posProvider.secret"
                                                label={
                                                    <span>
                                                        {getFieldLabel(
                                                            values.posProvider.providerName,
                                                            'secret'
                                                        )}
                                                    </span>
                                                }
                                                type="password"
                                                variant="filled"
                                                error={
                                                    touched.posProvider?.secret
                                                        ? errors.posProvider?.secret
                                                        : undefined
                                                }
                                                helperText={
                                                    touched.posProvider?.secret
                                                        ? errors.posProvider?.secret
                                                        : undefined
                                                }
                                                className={classes.inputContainer}
                                            />
                                        </>
                                    )}
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <h6 className={classes.sectionTitle}>
                                        {i18n
                                            .t('restaurant.webProvider')
                                            .toUpperCase()
                                            .toUpperCase()}
                                    </h6>
                                    <Box mb={2} />
                                    <FormControl
                                        className={classes.inputContainer}
                                        error={Boolean(
                                            touched.webProvider?.providerName &&
                                                errors.webProvider?.providerName
                                        )}
                                        fullWidth
                                    >
                                        <InputLabel
                                            id="web-provider-label"
                                            style={{ marginLeft: 12, marginTop: 5 }}
                                        >
                                            {i18n.t('form.provider')}
                                        </InputLabel>
                                        <Select
                                            variant="filled"
                                            labelId="web-provider-label"
                                            id="webProvider.providerName"
                                            name="webProvider.providerName"
                                            value={values.webProvider.providerName}
                                            onChange={handleProviderChange('webProvider')}
                                            onBlur={handleBlur}
                                            autoWidth
                                        >
                                            <MenuItem value={''}>{i18n.t('form.none')}</MenuItem>
                                            <MenuItem value={RestaurantPaymentProvider.Wallee}>
                                                {RestaurantPaymentProvider.Wallee}
                                            </MenuItem>
                                            <MenuItem value={RestaurantPaymentProvider.Datatrans}>
                                                {RestaurantPaymentProvider.Datatrans}
                                            </MenuItem>
                                        </Select>
                                        <FormHelperText>
                                            {errors.webProvider?.providerName}
                                        </FormHelperText>
                                    </FormControl>

                                    {values.webProvider.providerName ===
                                        RestaurantPaymentProvider.Wallee && (
                                        <>
                                            <Input
                                                name="webProvider.identity"
                                                label={
                                                    <span>
                                                        {getFieldLabel(
                                                            values.webProvider.providerName,
                                                            'identity'
                                                        )}
                                                    </span>
                                                }
                                                type="text"
                                                variant="filled"
                                                error={
                                                    touched.webProvider?.identity
                                                        ? errors.webProvider?.identity
                                                        : undefined
                                                }
                                                helperText={
                                                    touched.webProvider?.identity
                                                        ? errors.webProvider?.identity
                                                        : undefined
                                                }
                                                className={classes.inputContainer}
                                            />
                                            <Input
                                                name="webProvider.secret"
                                                label={
                                                    <span>
                                                        {getFieldLabel(
                                                            values.webProvider.providerName,
                                                            'secret'
                                                        )}
                                                    </span>
                                                }
                                                type="password"
                                                variant="filled"
                                                error={
                                                    touched.webProvider?.secret
                                                        ? errors.webProvider?.secret
                                                        : undefined
                                                }
                                                helperText={
                                                    touched.webProvider?.secret
                                                        ? errors.webProvider?.secret
                                                        : undefined
                                                }
                                                className={classes.inputContainer}
                                            />
                                            <Input
                                                name="webProvider.scope"
                                                label={
                                                    <span>
                                                        {getFieldLabel(
                                                            values.webProvider.providerName,
                                                            'scope'
                                                        )}
                                                    </span>
                                                }
                                                variant="filled"
                                                error={
                                                    touched.webProvider?.scope
                                                        ? errors.webProvider?.scope
                                                        : undefined
                                                }
                                                helperText={
                                                    touched.webProvider?.scope
                                                        ? errors.webProvider?.scope
                                                        : undefined
                                                }
                                                className={classes.inputContainer}
                                            />
                                        </>
                                    )}

                                    {values.webProvider.providerName ===
                                        RestaurantPaymentProvider.Datatrans && (
                                        <>
                                            <Input
                                                name="webProvider.identity"
                                                label={
                                                    <span>
                                                        {getFieldLabel(
                                                            values.webProvider.providerName,
                                                            'identity'
                                                        )}
                                                    </span>
                                                }
                                                variant="filled"
                                                error={
                                                    touched.webProvider?.identity
                                                        ? errors.webProvider?.identity
                                                        : undefined
                                                }
                                                helperText={
                                                    touched.webProvider?.identity
                                                        ? errors.webProvider?.identity
                                                        : undefined
                                                }
                                                className={classes.inputContainer}
                                            />
                                            <Input
                                                name="webProvider.secret"
                                                label={
                                                    <span>
                                                        {getFieldLabel(
                                                            values.webProvider.providerName,
                                                            'secret'
                                                        )}
                                                    </span>
                                                }
                                                type="text"
                                                variant="filled"
                                                error={
                                                    touched.webProvider?.secret
                                                        ? errors.webProvider?.secret
                                                        : undefined
                                                }
                                                helperText={
                                                    touched.webProvider?.secret
                                                        ? errors.webProvider?.secret
                                                        : undefined
                                                }
                                                className={classes.inputContainer}
                                            />
                                        </>
                                    )}
                                </Grid>
                            </Grid>

                            <Box className={classes.pymentSubmit}>
                                <Box component="span">{props.restaurantName}</Box>
                                <Button
                                    type="button"
                                    variant="contained"
                                    color="primary"
                                    onClick={() => {
                                        const touchedValues = {
                                            posProvider: {
                                                providerName: true,
                                                customProviders:
                                                    values.posProvider.customProviders.map(() => ({
                                                        providerName: true,
                                                    })),
                                                scope: true,
                                                identity: true,
                                                secret: true,
                                            },
                                            webProvider: {
                                                providerName: true,
                                                customProviders: [],
                                                scope: true,
                                                identity: true,
                                                secret: true,
                                            },
                                        };
                                        formikRef.current?.setTouched(touchedValues);
                                        setShowConfirmationModal(true);
                                    }}
                                    disabled={!dirty || !isValid || isSubmitting}
                                >
                                    {i18n.t('common.save')}
                                </Button>
                            </Box>
                        </Form>
                    );
                }}
            </Formik>
            <PopupConfirmation
                open={showConfirmationModal}
                close={() => setShowConfirmationModal(false)}
                title={i18n.t('common.attention')}
                description={i18n.t('warnings.paymentSettingsWarning')}
                activeBtn={i18n.t('common.save')}
                action={() => {
                    if (formikRef.current) {
                        const values = formikRef.current.values as FormValues;
                        formikRef.current.setTouched({
                            posProvider: {
                                providerName: true,
                                customProviders: values.posProvider.customProviders.map(() => ({
                                    providerName: true,
                                })),
                                scope: true,
                                identity: true,
                                secret: true,
                            },
                            webProvider: {
                                providerName: true,
                                customProviders: [],
                                scope: true,
                                identity: true,
                                secret: true,
                            },
                        });
                        formikRef.current.submitForm();
                    }
                    setShowConfirmationModal(false);
                }}
            />
            <PopupConfirmation
                open={showDeleteConfirmationModal}
                close={() => {
                    setShowDeleteConfirmationModal(false);
                    setCustomProviderToDelete(null);
                }}
                title={i18n.t('common.attention')}
                description={getCustomProviderDeleteWarning()}
                activeBtn={i18n.t('common.remove')}
                action={removeCustomProvider}
            />
        </>
    );
}
